export const formatUnitEach = (amount: number) => {
  if (amount > 9999) {
    return [(amount / 10000).toFixed(1), '万'];
  }
  return [amount, ''];
};

export const formatUnit = (amount: number) => {
  const [_amount, unit] = formatUnitEach(amount);
  return `${_amount}${unit}`;
};

export const formatNumber = (
  value: number | null | undefined,
  replace = '--'
) => {
  const _value = Number(value);
  if (isNaN(_value)) {
    return replace;
  }
  return value;
};

export function getDateDiff(date: Date | number) {
  const minute = 1000 * 60;
  const hour = minute * 60;
  const day = hour * 24;
  const month = day * 30;

  const now = new Date().getTime();
  const diffValue = now - (date as number);
  const monthNum = diffValue / month;
  const weekNum = diffValue / (7 * day);
  const dayNum = diffValue / day;
  const hourNum = diffValue / hour;
  const minNum = diffValue / minute;

  let result = '';
  if (monthNum >= 1 && monthNum < 4) {
    result = `${Math.floor(monthNum)} 个月前`;
  } else if (weekNum >= 1) {
    result = `${Math.floor(weekNum)} 周前`;
  } else if (dayNum >= 1 && dayNum < 2) {
    result = '昨天';
  } else if (dayNum >= 1) {
    result = Math.floor(dayNum) + ' ' + '天前';
  } else if (hourNum >= 1) {
    result = Math.floor(hourNum) + ' ' + '小时前';
  } else if (minNum >= 1) {
    result = Math.floor(minNum) + ' ' + '分钟前';
  } else if (minNum < 1) {
    result = '刚刚';
  }

  return result;
}

export function getDate(value: string | Date | number) {
  let date = new Date(value);
  if (!(date.getFullYear() > 0) || date.toString().indexOf('nvalid') >= 0) {
    if (typeof value === 'string') {
      date = new Date(value.replace(/-/g, '/'));
    }
  }
  return date;
}

/**
 * dateFormat 格式化时间戳
 * @param value 原始时间 字符串 时间戳 毫秒
 * @param format 自定义格式 yyyy-MM-dd hh:mm：ss
 * @param isDiff isDiff 是否使用时间差
 * @return
 */
export function dateFormat(
  value: string | Date | number,
  format = 'yyyy-MM-dd hh:mm',
  isDiff = false
) {
  // 返回处理后的值
  if (!value) return '';
  if (value.toString().indexOf('0000') === 0) return '';

  const date = getDate(value);

  // console.log(date, value);

  if (isDiff) {
    let format = getDateDiff(date);
    if (format) {
      return format;
    }
  }

  // console.log(format, 'format');

  const map = {
    M: date.getMonth() + 1, // 月份
    d: date.getDate(), // 日
    h: date.getHours(), // 小时
    m: date.getMinutes(), // 分
    s: date.getSeconds(), // 秒
    q: Math.floor((date.getMonth() + 3) / 3), // 季度
    S: date.getMilliseconds(), // 毫秒
  };
  format = format.replace(/([yMdhmsqS])(\1*)/g, function (all, t) {
    let v = map[t];
    if (v !== undefined) {
      if (all.length > 1) {
        v = `0${v}`;
        v = v.substring(v.length - 2);
      }
      return v;
    } else if (t === 'y') {
      return `${date.getFullYear()}`.substring(4 - all.length);
    }
    return all;
  });
  return format;
}

export function imDateFormat(value: string | Date | number) {
  // 返回处理后的值
  if (!value) return '';
  if (value.toString().indexOf('0000') === 0) return '';

  const date = getDate(value).getTime();

  const dateArr = dateFormat(date, 'yyyy-MM-dd hh:mm').split(' ');

  const minute = 1000 * 60;
  const hour = minute * 60;
  const day = hour * 24;

  const now = new Date().getTime();
  const diffValue = now - date;
  const dayNum = diffValue / day;

  let result = dateArr.join(' ');

  if (dayNum < 1) {
    result = '今天 ' + dateArr[1];
  } else if (dayNum >= 1 && dayNum < 2) {
    result = '昨天 ' + dateArr[1];
  }

  return result;
}

export function placeholderFormat(value: any, symbol = '--') {
  if (typeof value === 'string') {
    if (value === '-') {
      return symbol;
    }
    return value || symbol;
  } else if (value === void 0 || value === null) {
    return symbol;
  }
  return value;
}

// 深拷贝
// 深拷贝
type DeepCopy<T> = T extends Date
  ? Date
  : T extends Array<infer U>
  ? Array<DeepCopy<U>>
  : T extends object
  ? { [K in keyof T]: DeepCopy<T[K]> }
  : T;

export function deepCopy<T>(obj: T, specifiedProtoKeys: string[] = [], hash = new WeakMap<object, any>()): DeepCopy<T> {
  if (obj === null || typeof obj !== 'object') {
    return obj as any;
  }

  if (hash.has(obj)) {
    return hash.get(obj) as any;
  }

  if (obj instanceof Date) {
    return new Date(obj) as any;
  }

  if (Array.isArray(obj)) {
    const copy = [] as any;
    hash.set(obj, copy);
    for (let i = 0; i < (obj as any).length; i++) {
      copy[i] = deepCopy((obj as any)[i], specifiedProtoKeys, hash);
    }
    return copy as DeepCopy<T>;
  }

  const copy = {} as any;
  hash.set(obj, copy);
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      copy[key] = deepCopy((obj as any)[key], specifiedProtoKeys, hash);
    }
  }

  // 新增逻辑：处理原型上指定属性的拷贝
  const proto = Object.getPrototypeOf(obj);
  if (proto) {
    specifiedProtoKeys.forEach(protoKey => {
      if (Object.prototype.hasOwnProperty.call(proto, protoKey)) {
        const desc = Object.getOwnPropertyDescriptor(proto, protoKey);
        if (desc) {
          Object.defineProperty(copy, protoKey, {
            ...desc,
            value: deepCopy(desc.value, specifiedProtoKeys, hash)
          });
        }
      }
    });
  }

  return copy as DeepCopy<T>;
}

// 生产永不重复的id
export function dateId(index: number = 0): string {
  const timestamp = Date.now();
  const randomPart = Math.random().toString(4).substring(2, 10);
  const uniqueValue = `${timestamp}-${index}-${randomPart}`;
  return uniqueValue;
}

// 取数组的第一项
export function getFirstItem<T>(input: T[] | string): T | string {
  if (Array.isArray(input)) {
    // 如果 input 是数组，返回第一个元素（如果有）
    return input[0] as T;
  } else {
    // 如果 input 是字符串，直接返回
    return input;
  }
}



/**
 * 时间格式转换函数
 * @param input 输入时间（支持多种格式）
 * @param format 目标格式（默认：'YYYY-MM-DD HH:mm:ss'）
 * @returns 格式化后的时间字符串
 */
type DateInput =
  | Date
  | number
  | string
  | (number | string)[]; // 支持混合类型的数组

type FormatToken =
  | 'YYYY' | 'YY'   // 年
  | 'MM' | 'M'      // 月
  | 'DD' | 'D'      // 日
  | 'HH' | 'H'      // 时
  | 'mm' | 'm'      // 分
  | 'ss' | 's'      // 秒
  | 'SSS'           // 毫秒
  | string;

/**
 * 增强版时间格式转换函数
 * @param input 输入时间（支持包含时间的数组格式）
 * @param format 目标格式（默认：'YYYY-MM-DD HH:mm:ss'）
 * @returns 格式化后的时间字符串
 */
export function formatDateTime(
  input: DateInput,
  format: string = 'YYYY-MM-DD HH:mm:ss'
): string {
  const date = safeParse(input);
  return formatDate(date, format);
}

// 安全解析核心方法
function safeParse(input: DateInput=new Date()): Date {
  try {
    if (input instanceof Date) return input;

    if (typeof input === 'number') {
      // 处理时间戳（兼容秒级时间戳）
      return input > 1e12 ? new Date(input) : new Date(input * 1000);
    }

    if (Array.isArray(input)) {
      // 转换所有元素为数字并验证有效性
      const numbers = input.map(n => {
        const num = Number(n);
        if (isNaN(num)) throw new Error('包含非数字的数组元素');
        return num;
      });

      // 解构带默认值（年, 月, 日=1, 时=0, 分=0, 秒=0）
      const [y, m, d = 1, h = 0, min = 0, s = 0] = numbers;
      return new Date(y, m - 1, d, h, min, s);
    }

    // 增强字符串处理
    return parseComplexString(input);
  } catch (e) {
    throw new Error(`日期解析失败: ${e.message}`);
  }
}

// 处理复杂字符串格式
function parseComplexString(str: string): Date {
  // 统一处理步骤
  const processed = str
    .replace(/\s+/g, '')      // 移除所有空白字符
    .replace(/[年月日时分秒]/g, '-') // 统一替换分隔符
    .replace(/(\d)-(\d)/g, (_, a, b) => `${a.padStart(2, '0')}-${b.padStart(2, '0')}`)
    .replace(/-+/g, '-')      // 合并连续分隔符
    .replace(/T/g, ' ')       // 处理ISO格式
    .replace(/[\/\\]/g, '-'); // 统一分隔符

  // 正则表达式匹配增强版
  const pattern = /^(\d{4})-?(\d{1,2})-?(\d{0,2})?-?(\d{1,2})?-?(\d{1,2})?-?(\d{1,2})?/;
  const match = processed.match(pattern);

  if (match) {
    const [, year, month, day = '1', hour = '0', minute = '0', second = '0'] = match;
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second)
    );
  }

  throw new Error(`无法解析的字符串格式: ${str}`);
}

// 格式化核心方法（保持不变）
function formatDate(date: Date, format: string): string {
  const pad = (n: number, length = 2) => n.toString().padStart(length, '0');

  const parts = {
    YYYY: pad(date.getFullYear(), 4),
    YY: pad(date.getFullYear() % 100),
    MM: pad(date.getMonth() + 1),
    M: (date.getMonth() + 1).toString(),
    DD: pad(date.getDate()),
    D: date.getDate().toString(),
    HH: pad(date.getHours()),
    H: date.getHours().toString(),
    mm: pad(date.getMinutes()),
    m: date.getMinutes().toString(),
    ss: pad(date.getSeconds()),
    s: date.getSeconds().toString(),
    SSS: pad(date.getMilliseconds(), 3)
  };

  return format.replace(
    /YYYY|YY|MM|M|DD|D|HH|H|mm|m|ss|s|SSS/g,
    (match) => parts[match as FormatToken] || match
  );
}



